import React, { Profiler, ProfilerOnRenderCallback } from 'react';

interface PerformanceProfilerProps {
  id: string;
  children: React.ReactNode;
  logThreshold?: number; // Only log if render time exceeds this (ms)
}

export const PerformanceProfiler: React.FC<PerformanceProfilerProps> = ({
  id,
  children,
  logThreshold = 16 // Default to 16ms (60fps threshold)
}) => {
  // In production, skip React.Profiler entirely to avoid its runtime overhead
  if (process.env.NODE_ENV !== 'development') {
    return <>{children}</>;
  }
  const onRenderCallback: ProfilerOnRenderCallback = (
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime
  ) => {
    // Only log in development mode
    if (process.env.NODE_ENV !== 'development') return;
    
    // Only log if render time exceeds threshold
    if (actualDuration < logThreshold) return;

    const performanceData = {
      component: id,
      phase, // "mount" or "update"
      actualDuration: `${actualDuration.toFixed(2)}ms`,
      baseDuration: `${baseDuration.toFixed(2)}ms`,
      startTime: `${startTime.toFixed(2)}ms`,
      commitTime: `${commitTime.toFixed(2)}ms`
    };

    // Color code based on severity
    const color = actualDuration > 100 ? '#ff0000' : 
                  actualDuration > 50 ? '#ff6600' : 
                  actualDuration > 16 ? '#ffaa00' : '#00aa00';

    console.groupCollapsed(
      `%c⚡ ${id} - ${phase} (${actualDuration.toFixed(2)}ms)`,
      `color: ${color}; font-weight: bold;`
    );
    console.table(performanceData);
    
    if (actualDuration > 100) {
      console.warn('⚠️ Slow render detected! Consider optimization.');
    }
    
    console.groupEnd();
  };

  return (
    <Profiler id={id} onRender={onRenderCallback}>
      {children}
    </Profiler>
  );
};

// Hook for manual performance tracking
export const usePerformanceMonitor = (componentName: string) => {
  const startMark = `${componentName}-start`;
  const endMark = `${componentName}-end`;
  const measureName = `${componentName}-measure`;

  const startTracking = () => {
    if (process.env.NODE_ENV !== 'development') return;
    performance.mark(startMark);
  };

  const endTracking = () => {
    if (process.env.NODE_ENV !== 'development') return;
    
    try {
      performance.mark(endMark);
      performance.measure(measureName, startMark, endMark);
      
      const measure = performance.getEntriesByName(measureName)[0];
      if (measure && measure.duration > 16) {
        console.log(
          `%c⚡ ${componentName}: ${measure.duration.toFixed(2)}ms`,
          `color: ${measure.duration > 100 ? '#ff0000' : '#ffaa00'}; font-weight: bold;`
        );
      }
      
      // Clean up
      performance.clearMarks(startMark);
      performance.clearMarks(endMark);
      performance.clearMeasures(measureName);
    } catch (e) {
      // Ignore errors if marks don't exist
    }
  };

  return { startTracking, endTracking };
};
